import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit, computed, input, output, signal } from '@angular/core';
import { CondicaoRegra, OpcaoCondicao, RegraElegibilidade } from '../../../../core/models/parametros/extrato-desligamento.model';
import { MENSAGEM_NAO_ELEGIBILIDADE_MAXIMO } from '../../../../core/utils/parametros/extrato-desligamento.defaults';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';

/** O que o modal devolve: as condições escolhidas e a mensagem de não elegibilidade. */
export type RegraEditada = Pick<RegraElegibilidade, 'condicoes' | 'mensagem'>;

/**
 * Modal de "Adicionar regra" / "Editar regra" de um instituto sobre o `vivest-modal`: condições
 * (todas precisam ser atendidas) e a mensagem exibida ao participante que não as atende. As
 * condições parametrizáveis (ex.: tempo mínimo de vinculação) pedem o valor ao serem marcadas.
 */
@Component({
  selector: 'app-regra-elegibilidade-modal',
  standalone: true,
  imports: [VivestModalAreaVisivelDirective],
  // Web components do design system Vivest (modal, checkbox, button).
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './regra-elegibilidade-modal.component.html',
  styleUrl: './regra-elegibilidade-modal.component.scss',
})
export class RegraElegibilidadeModalComponent implements OnInit {
  /** Nome do instituto, para o título. */
  readonly instituto = input.required<string>();
  /** Regra em edição; `null` cria uma regra nova. */
  readonly regra = input<RegraElegibilidade | null>(null);
  /** Condições do catálogo, na ordem da tela. */
  readonly condicoes = input.required<readonly OpcaoCondicao[]>();

  readonly salvar = output<RegraEditada>();
  readonly fechar = output<void>();

  /** Condições inativas no catálogo só aparecem quando a regra já as usa. */
  readonly opcoes = computed(() => {
    const daRegra = new Set(this.regra()?.condicoes.map((condicao) => condicao.idCondicao) ?? []);
    return this.condicoes().filter((opcao) => !opcao.inativa || daRegra.has(opcao.value));
  });
  readonly mensagemMaximo = MENSAGEM_NAO_ELEGIBILIDADE_MAXIMO;

  /** Condições marcadas e o valor digitado (texto) das parametrizáveis; vazio nas demais. */
  readonly marcadas = signal<ReadonlyMap<number, string>>(new Map());
  readonly mensagem = signal('');
  /** Os erros só aparecem depois de uma tentativa de salvar. */
  readonly tentouSalvar = signal(false);

  readonly titulo = computed(() => `${this.regra() ? 'Editar' : 'Adicionar'} regra de ${this.instituto()}`);
  readonly semCondicao = computed(() => this.marcadas().size === 0);

  /** Erro do valor de cada condição parametrizável marcada; sem erro, não entra no mapa. */
  readonly errosValor = computed(() => {
    const erros = new Map<number, string>();
    for (const opcao of this.opcoes()) {
      const valor = this.marcadas().get(opcao.value);
      const parametro = opcao.parametro;
      if (parametro && valor !== undefined) {
        const numero = Number(valor);
        if (!valor || numero < parametro.minimo || numero > parametro.maximo) {
          erros.set(opcao.value, `Informe de ${parametro.minimo} a ${parametro.maximo} ${parametro.unidade}.`);
        }
      }
    }
    return erros;
  });

  ngOnInit(): void {
    const regra = this.regra();
    if (regra) {
      this.marcadas.set(new Map(regra.condicoes.map((condicao) => [condicao.idCondicao, condicao.valor?.toString() ?? ''])));
      this.mensagem.set(regra.mensagem);
    }
  }

  marcada(opcao: OpcaoCondicao): boolean {
    return this.marcadas().has(opcao.value);
  }

  valor(opcao: OpcaoCondicao): string {
    return this.marcadas().get(opcao.value) ?? '';
  }

  /** Marcar uma condição parametrizável já preenche o valor padrão (ex.: 3 anos). */
  alternar(opcao: OpcaoCondicao, marcada: boolean): void {
    const proximas = new Map(this.marcadas());
    if (marcada) {
      proximas.set(opcao.value, opcao.parametro ? String(opcao.parametro.padrao) : '');
    } else {
      proximas.delete(opcao.value);
    }
    this.marcadas.set(proximas);
  }

  /** Só dígitos ficam no campo. */
  digitarValor(opcao: OpcaoCondicao, campo: HTMLInputElement): void {
    const digitos = campo.value.replace(/\D/g, '');
    campo.value = digitos;
    this.marcadas.set(new Map(this.marcadas()).set(opcao.value, digitos));
  }

  confirmar(): void {
    this.tentouSalvar.set(true);
    if (this.semCondicao() || this.errosValor().size) {
      return;
    }
    // Condições na ordem da lista, não na ordem em que foram marcadas.
    const condicoes: CondicaoRegra[] = this.opcoes()
      .filter((opcao) => this.marcada(opcao))
      .map((opcao) => (opcao.parametro ? { idCondicao: opcao.value, valor: Number(this.valor(opcao)) } : { idCondicao: opcao.value }));
    this.salvar.emit({ condicoes, mensagem: this.mensagem().trim() });
  }
}
